import { common, grey } from '@mui/material/colors';
import { alpha, createTheme, darken, lighten, responsiveFontSizes } from '@mui/material/styles';
import { useMemo } from 'react';

import type { defaultColors } from '../../sharedInterfaces/defaultColors';

// -------------------------
// Erweiterung für die Palette
// -------------------------
// -------------------------
// TypeScript Module Augmentation
// -------------------------
declare module '@mui/material/styles' {
    interface Palette {
        // Custom colors
        accent?: { main: string; contrastText: string };

        // Custom gradients
        gradient?: string;
        subtleBackground?: string;
        primaryToSecondaryGradient?: string;

        // Level colors
        levels?: {
            1: string;
            2: string;
            3: string;
            4: string;
            5: string;
        };

        // Primary color gradation for level backgrounds
        levelBackgrounds?: {
            1: string;
            2: string;
            3: string;
            4: string;
            5: string;
        };

        // Enhanced contrast helpers for tiles and highlights
        tileBackground?: {
            primary: string;
            secondary: string;
            accent: string;
            success: string;
            warning: string;
            error: string;
            info: string;
        };
        tileBorder?: {
            primary: string;
            secondary: string;
            accent: string;
            success: string;
            warning: string;
            error: string;
            info: string;
        };

        // Notification colors
        onboarding?: { main: string; contrastText: string };
        skill?: { main: string; contrastText: string };
        certificate?: { main: string; contrastText: string };
        project?: { main: string; contrastText: string };
        meeting?: { main: string; contrastText: string };
    }

    interface PaletteOptions {
        // Custom colors
        accent?: { main: string; contrastText: string };

        // Custom gradients
        gradient?: string;
        subtleBackground?: string;
        primaryToSecondaryGradient?: string;

        // Level colors
        levels?: {
            1: string;
            2: string;
            3: string;
            4: string;
            5: string;
        };

        // Primary color gradation for level backgrounds
        levelBackgrounds?: {
            1: string;
            2: string;
            3: string;
            4: string;
            5: string;
        };

        // Enhanced contrast helpers for tiles and highlights
        tileBackground?: {
            primary: string;
            secondary: string;
            accent: string;
            success: string;
            warning: string;
            error: string;
            info: string;
        };
        tileBorder?: {
            primary: string;
            secondary: string;
            accent: string;
            success: string;
            warning: string;
            error: string;
            info: string;
        };

        // Notification colors
        onboarding?: { main: string; contrastText: string };
        skill?: { main: string; contrastText: string };
        certificate?: { main: string; contrastText: string };
        project?: { main: string; contrastText: string };
        meeting?: { main: string; contrastText: string };
    }
}

// -------------------------
// Erweiterung für neue Button-Varianten
// -------------------------
declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        animatedBorder: true;
        white: true;
        primary: true;
        secondary: true;
        gradient: true;
    }
    interface ButtonPropsColorOverrides {
        white: true;
        gradient: true;
    }
}

// -------------------------
// Erweiterung für neue Chip-Variante
// -------------------------
declare module '@mui/material/Chip' {
    interface ChipPropsVariantOverrides {
        gradientChip: true;
    }
}

interface GenerateThemeProps {
    isDarkMode: boolean;
    primaryColor: string;
    secondaryColor: string;
    defaultColors: typeof defaultColors;
}

export function generateTheme({ isDarkMode, primaryColor, secondaryColor, defaultColors }: GenerateThemeProps) {
    return useMemo(() => {
        // -------------------------
        // Accent Color Calculation
        // -------------------------
        const accentFrom = (() => {
            const toRgb = (h: string) => {
                const n = parseInt(h.slice(1), 16);
                return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
            };
            const hex = (rgb: number[]) => '#' + rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('');

            return (p: string, s: string) => {
                const P = toRgb(p);
                const S = toRgb(s);
                // 70 % Primary + 30 % Secondary
                const mix = P.map((_, i) => P[i] * 0.7 + S[i] * 0.3);
                // 15 % aufhellen
                const light = mix.map((v) => v + (255 - v) * 0.15);
                // zu hell? 5 % runter
                if (light.every((v) => v > 240)) light.forEach((_, i) => (light[i] *= 0.95));
                return hex(light);
            };
        })();

        // -------------------------
        // Typography Scale Configuration
        // -------------------------
        const golden = 1.618;
        const base = 1;

        const typographyScale = {
            h1: base * golden * golden,
            h2: 2,
            h3: 1.5,
            h4: 1.333,
            h5: 1.25,
            h6: 1.125,
            body1: base,
            body2: base * 0.875,
        };

        const lineHeights = {
            heading: 1.2,
            body: 1.6,
            small: 1.5,
        };

        const animatedBorderStyles = {
            position: 'relative' as const,
            zIndex: 1,
            overflow: 'hidden' as const,
            borderRadius: 16,
            fontWeight: 500,
            lineHeight: 1.5,
            transition: 'transform 0.2s ease',
            backgroundColor: isDarkMode ? alpha(defaultColors.dark.paper, 0.3) : alpha('#f5f5f5', 0.3),
            color: isDarkMode ? defaultColors.dark.text : 'rgba(0, 0, 0, 0.7)',
            '&:hover': {
                transform: 'scale(1.03)',
            },
            '&::before': {
                content: '""',
                position: 'absolute' as const,
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                borderRadius: 'inherit',
                padding: '1.5px',
                background: `radial-gradient(
          circle at var(--mouse-x, 50%) var(--mouse-y, 50%),
          ${primaryColor},
          ${secondaryColor}
        )`,
                backgroundSize: '200% 200%',
                backgroundPosition: 'center',
                zIndex: -1,
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
                pointerEvents: 'none' as const,
                transition: 'background-position 0.4s ease',
            },
        };

        // -------------------------
        // Create Base Theme
        // -------------------------
        let theme = createTheme({
            shape: { borderRadius: 3 },

            // -------------------------
            // Color Palette
            // -------------------------
            palette: {
                mode: isDarkMode ? 'dark' : 'light',

                // Primary & Secondary Colors
                primary: {
                    main: isDarkMode ? lighten(primaryColor, 0.2) : primaryColor,
                    light: lighten(primaryColor, isDarkMode ? 0.4 : 0.2),
                    dark: darken(primaryColor, 0.2),
                    contrastText: '#ffffff',
                },
                secondary: {
                    main: secondaryColor,
                    light: lighten(secondaryColor, 0.2),
                    dark: darken(secondaryColor, 0.2),
                    contrastText: isDarkMode ? '#ffffff' : '#333333',
                },

                // Background Colors
                background: {
                    default: isDarkMode ? defaultColors.dark.background : defaultColors.light.background,
                    paper: isDarkMode ? defaultColors.dark.paper : defaultColors.light.paper,
                },

                // Text Colors
                text: {
                    primary: isDarkMode ? '#dfdfd6' : defaultColors.light.text,
                    secondary: isDarkMode ? '#c5c5bd' : '#666666',
                    disabled: '#9e9e9e',
                },

                // Status Colors
                success: { main: '#0FC89D', contrastText: '#ffffff' },
                error: { main: '#EA4E42', contrastText: '#ffffff' },
                warning: { main: '#ffb74d', contrastText: '#333333' },
                info: { main: '#29b6f6', contrastText: '#ffffff' },

                // Grey Palette (4 essential tones from MUI)
                grey: {
                    300: grey[300], // Light grey
                    500: grey[500], // Medium grey
                    700: grey[700], // Dark grey (tooltips)
                    900: grey[900], // Very dark grey
                },
                common,

                // Level Colors (theme-aware) - 5 levels
                levels: {
                    1: isDarkMode ? '#B8E6D3' : '#E8F8F5', // Level 1 - lightest
                    2: isDarkMode ? '#7DCCB8' : '#A0FFDD', // Level 2 - light
                    3: isDarkMode ? '#4FB3A8' : '#63FFD3', // Level 3 - medium
                    4: isDarkMode ? '#009B82' : '#00CBA2', // Level 4 - darkest
                    5: isDarkMode ? '#00B89A' : '#00E8B9', // Level 5 - darker
                },

                // Primary color gradation for level backgrounds (like Items) - 5 levels
                levelBackgrounds: {
                    1: alpha(isDarkMode ? lighten(primaryColor, 0.2) : primaryColor, 0.15),
                    2: alpha(isDarkMode ? lighten(primaryColor, 0.2) : primaryColor, 0.25),
                    3: alpha(isDarkMode ? lighten(primaryColor, 0.2) : primaryColor, 0.35),
                    4: alpha(isDarkMode ? lighten(primaryColor, 0.2) : primaryColor, 0.45),
                    5: alpha(isDarkMode ? lighten(primaryColor, 0.2) : primaryColor, 0.55),
                },

                // Enhanced contrast helpers for tiles and highlights
                tileBackground: {
                    primary: alpha(isDarkMode ? lighten(primaryColor, 0.2) : primaryColor, isDarkMode ? 0.25 : 0.15),
                    secondary: alpha(
                        isDarkMode ? lighten(secondaryColor, 0.2) : secondaryColor,
                        isDarkMode ? 0.25 : 0.15,
                    ),
                    accent: alpha(isDarkMode ? '#ffcc80' : '#ffb74d', isDarkMode ? 0.25 : 0.15),
                    success: alpha('#0FC89D', isDarkMode ? 0.25 : 0.15),
                    warning: alpha('#ffb74d', isDarkMode ? 0.25 : 0.15),
                    error: alpha('#f44336', isDarkMode ? 0.25 : 0.15),
                    info: alpha('#29b6f6', isDarkMode ? 0.25 : 0.15),
                },
                tileBorder: {
                    primary: alpha(isDarkMode ? lighten(primaryColor, 0.2) : primaryColor, isDarkMode ? 0.6 : 0.4),
                    secondary: alpha(
                        isDarkMode ? lighten(secondaryColor, 0.2) : secondaryColor,
                        isDarkMode ? 0.6 : 0.4,
                    ),
                    accent: alpha(isDarkMode ? '#ffcc80' : '#ffb74d', isDarkMode ? 0.6 : 0.4),
                    success: alpha('#0FC89D', isDarkMode ? 0.6 : 0.4),
                    warning: alpha('#ffb74d', isDarkMode ? 0.6 : 0.4),
                    error: alpha('#f44336', isDarkMode ? 0.6 : 0.4),
                    info: alpha('#29b6f6', isDarkMode ? 0.6 : 0.4),
                },

                // Custom Colors
                accent: {
                    main: accentFrom(primaryColor, secondaryColor),
                    contrastText: isDarkMode ? '#1a1a1a' : '#ffffff',
                },
                onboarding: { main: isDarkMode ? lighten(primaryColor, 0.2) : primaryColor, contrastText: '#ffffff' },
                skill: { main: '#4dcea9', contrastText: '#ffffff' },
                certificate: {
                    main: isDarkMode ? lighten(secondaryColor, 0.2) : secondaryColor,
                    contrastText: isDarkMode ? '#ffffff' : '#333333',
                },
                project: { main: '#29b6f6', contrastText: '#ffffff' },
                meeting: { main: '#ffb74d', contrastText: '#333333' },

                // Dividers & Gradients
                divider: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                gradient: isDarkMode
                    ? 'linear-gradient(135deg, #252526 0%, #1e1e1e 100%)'
                    : `linear-gradient(to bottom right, ${alpha(primaryColor, 0.15)}, ${alpha(secondaryColor, 0.15)})`,
                primaryToSecondaryGradient: `linear-gradient(135deg, ${primaryColor} 0%, ${alpha(secondaryColor, 0.7)} 100%)`,
                subtleBackground: isDarkMode ? '#333333' : '#f0f5f8',
            },

            // -------------------------
            // Typography Configuration
            // -------------------------
            typography: {
                fontFamily: [
                    'Inter',
                    '-apple-system',
                    'BlinkMacSystemFont',
                    '"Segoe UI"',
                    'Roboto',
                    '"Helvetica Neue"',
                    'Arial',
                    'sans-serif',
                    '"Apple Color Emoji"',
                    '"Segoe UI Emoji"',
                    '"Segoe UI Symbol"',
                ].join(','),
                allVariants: {
                    color: isDarkMode ? '#dfdfd6' : '#333333',
                },
                // Headings
                h1: {
                    fontSize: `${typographyScale.h1}rem`,
                    lineHeight: lineHeights.heading,
                    letterSpacing: '-0.04em',
                    fontWeight: 500,
                },
                h2: {
                    fontSize: `${typographyScale.h2}rem`,
                    lineHeight: lineHeights.heading,
                    fontWeight: 400,
                    letterSpacing: '-0.015em',
                },
                h3: {
                    fontSize: `${typographyScale.h3}rem`,
                    lineHeight: lineHeights.heading,
                    fontWeight: 400,
                    letterSpacing: 0,
                },
                h4: {
                    fontSize: `${typographyScale.h4}rem`,
                    lineHeight: lineHeights.heading,
                    fontWeight: 400,
                    letterSpacing: '-0.015em',
                },
                h5: {
                    fontSize: `${typographyScale.h5}rem`,
                    lineHeight: lineHeights.heading,
                    fontWeight: 400,
                    letterSpacing: '-0.015em',
                },
                h6: {
                    fontSize: `${typographyScale.h6}rem`,
                    lineHeight: lineHeights.heading,
                    fontWeight: 200,
                    letterSpacing: '-0.015em',
                },

                // Subtitles
                subtitle1: {
                    fontSize: '1rem',
                    lineHeight: lineHeights.small,
                    fontWeight: 200,
                    letterSpacing: '-0.025em',
                },
                subtitle2: {
                    fontSize: '0.875rem',
                    lineHeight: lineHeights.small,
                    fontWeight: 500,
                    letterSpacing: '-0.005em',
                },

                // Body Text
                body1: {
                    fontSize: `${typographyScale.body1}rem`,
                    lineHeight: lineHeights.body,
                    fontWeight: 400,
                },
                body2: {
                    fontSize: `${typographyScale.body2}rem`,
                    lineHeight: lineHeights.small,
                    fontWeight: 400,
                },

                // UI Elements
                button: {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    textTransform: 'none',
                },
                caption: {
                    fontSize: '0.75rem',
                    lineHeight: lineHeights.small,
                    fontWeight: 400,
                },
            },

            // -------------------------
            // Component Overrides
            // -------------------------
            components: {
                MuiButton: {
                    defaultProps: {
                        variant: 'outlined',
                    },
                    styleOverrides: {
                        root: {
                            textTransform: 'none',
                            borderRadius: 8,
                            fontFamily: 'Inter, sans-serif',
                            fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"',
                            position: 'relative',
                            zIndex: 1,
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            // Better accessibility in dark mode
                            ...(isDarkMode && {
                                color: lighten(primaryColor, 0.2),
                                borderColor: lighten(primaryColor, 0.2),
                                '&:hover': {
                                    backgroundColor: alpha(lighten(primaryColor, 0.2), 0.1),
                                    borderColor: lighten(primaryColor, 0.3),
                                    color: lighten(primaryColor, 0.3),
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 2px 8px ${alpha(lighten(primaryColor, 0.2), 0.15)}`,
                                },
                            }),
                            // Light mode styles
                            ...(!isDarkMode && {
                                '&:hover': {
                                    backgroundColor: `${alpha(primaryColor, 0.04)}`,
                                    borderColor: `${alpha(primaryColor, 0.5)}`,
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 2px 8px ${alpha(primaryColor, 0.15)}`,
                                },
                            }),
                        },
                        contained: {
                            // Light mode hover - lighter primary color
                            ...(!isDarkMode && {
                                '&:hover': {
                                    backgroundColor: lighten(primaryColor, 0.1),
                                    color: 'primary.contrastText',
                                },
                            }),
                            // Better contrast for contained buttons in dark mode
                            ...(isDarkMode && {
                                backgroundColor: lighten(primaryColor, 0.2),
                                color: '#ffffff',
                                '&:hover': {
                                    backgroundColor: lighten(primaryColor, 0.3),
                                    color: '#ffffff',
                                },
                            }),
                        },
                    },
                    variants: [
                        {
                            props: { variant: 'animatedBorder', size: 'small' },
                            style: {
                                ...animatedBorderStyles,
                                padding: '6px 12px',
                                fontSize: '0.75rem',
                            },
                        },
                        {
                            props: { variant: 'animatedBorder', size: 'medium' },
                            style: {
                                ...animatedBorderStyles,
                                padding: '6px 16px',
                                fontSize: '0.875rem',
                            },
                        },
                        {
                            props: { variant: 'animatedBorder', size: 'large' },
                            style: {
                                ...animatedBorderStyles,
                                padding: '8px 22px',
                                fontSize: '1rem',
                            },
                        },
                        // White variant - contained
                        {
                            props: { variant: 'white' },
                            style: {
                                backgroundColor: '#ffffff',
                                color: '#000000',
                                border: `1px solid ${isDarkMode ? alpha('#ffffff', 0.3) : '#000000'}`,
                                '&:hover': {
                                    backgroundColor: alpha('#ffffff', 0.9),
                                    color: '#000000',
                                    borderColor: isDarkMode ? '#ffffff' : '#000000',
                                },
                            },
                        },
                        // White variant - outlined
                        {
                            props: { variant: 'outlined', color: 'white' },
                            style: {
                                backgroundColor: 'transparent',
                                color: '#ffffff',
                                border: `1px solid #ffffff`,
                                '&:hover': {
                                    backgroundColor: alpha('#ffffff', 0.1),
                                    color: '#ffffff',
                                    borderColor: '#ffffff',
                                },
                            },
                        },
                        // Primary variant - contained
                        {
                            props: { variant: 'primary' },
                            style: {
                                backgroundColor: isDarkMode ? lighten(primaryColor, 0.2) : primaryColor,
                                color: '#ffffff',
                                border: 'none',
                                '&:hover': {
                                    backgroundColor: isDarkMode
                                        ? lighten(primaryColor, 0.1)
                                        : darken(primaryColor, 0.1),
                                    color: '#ffffff',
                                },
                            },
                        },
                        // Primary variant - outlined
                        {
                            props: { variant: 'outlined', color: 'primary' },
                            style: {
                                backgroundColor: 'transparent',
                                color: isDarkMode ? lighten(primaryColor, 0.2) : primaryColor,
                                border: `1px solid ${isDarkMode ? lighten(primaryColor, 0.2) : primaryColor}`,
                                '&:hover': {
                                    backgroundColor: alpha(isDarkMode ? lighten(primaryColor, 0.2) : primaryColor, 0.1),
                                    color: isDarkMode ? lighten(primaryColor, 0.2) : primaryColor,
                                    borderColor: isDarkMode ? lighten(primaryColor, 0.2) : primaryColor,
                                },
                            },
                        },
                        // Secondary variant - contained
                        {
                            props: { variant: 'secondary' },
                            style: {
                                backgroundColor: isDarkMode ? lighten(secondaryColor, 0.2) : secondaryColor,
                                color: isDarkMode ? '#000000' : '#333333',
                                border: 'none',
                                '&:hover': {
                                    backgroundColor: isDarkMode
                                        ? lighten(secondaryColor, 0.1)
                                        : darken(secondaryColor, 0.1),
                                    color: isDarkMode ? '#000000' : '#333333',
                                },
                            },
                        },
                        // Secondary variant - outlined
                        {
                            props: { variant: 'outlined', color: 'secondary' },
                            style: {
                                backgroundColor: 'transparent',
                                color: isDarkMode ? lighten(secondaryColor, 0.2) : secondaryColor,
                                border: `1px solid ${isDarkMode ? lighten(secondaryColor, 0.2) : secondaryColor}`,
                                '&:hover': {
                                    backgroundColor: alpha(
                                        isDarkMode ? lighten(secondaryColor, 0.2) : secondaryColor,
                                        0.1,
                                    ),
                                    color: isDarkMode ? lighten(secondaryColor, 0.2) : secondaryColor,
                                    borderColor: isDarkMode ? lighten(secondaryColor, 0.2) : secondaryColor,
                                },
                            },
                        },
                        // Gradient variant - contained
                        {
                            props: { variant: 'gradient' },
                            style: {
                                background: isDarkMode
                                    ? `linear-gradient(135deg, ${lighten(primaryColor, 0.2)} 0%, ${lighten(secondaryColor, 0.2)} 100%)`
                                    : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                                color: '#ffffff',
                                border: 'none',
                                '&:hover': {
                                    background: isDarkMode
                                        ? `linear-gradient(135deg, ${lighten(primaryColor, 0.1)} 0%, ${lighten(secondaryColor, 0.1)} 100%)`
                                        : `linear-gradient(135deg, ${darken(primaryColor, 0.1)} 0%, ${darken(secondaryColor, 0.1)} 100%)`,
                                    color: '#ffffff',
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 12px ${alpha(primaryColor, 0.3)}`,
                                },
                            },
                        },
                        // Gradient variant - outlined
                        {
                            props: { variant: 'outlined', color: 'gradient' },
                            style: {
                                backgroundColor: 'transparent',
                                background: `linear-gradient(135deg, transparent, transparent)`,
                                border: `2px solid transparent`,
                                backgroundImage: isDarkMode
                                    ? `linear-gradient(135deg, ${lighten(primaryColor, 0.2)} 0%, ${lighten(secondaryColor, 0.2)} 100%)`
                                    : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                                backgroundOrigin: 'border-box',
                                backgroundClip: 'padding-box, border-box',
                                color: isDarkMode ? lighten(primaryColor, 0.2) : primaryColor,
                                '&:hover': {
                                    backgroundColor: alpha(
                                        isDarkMode ? lighten(primaryColor, 0.2) : primaryColor,
                                        0.05,
                                    ),
                                    color: isDarkMode ? lighten(primaryColor, 0.1) : darken(primaryColor, 0.1),
                                },
                            },
                        },
                    ],
                },
                MuiTextField: {
                    styleOverrides: {
                        root: {
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderRadius: '4px',
                                },
                            },
                        },
                    },
                },
                MuiOutlinedInput: {
                    defaultProps: {
                        size: 'small',
                    },
                    styleOverrides: {
                        root: {
                            borderRadius: '12px',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderRadius: '12px',
                            },
                            '&.MuiInputBase-root:has(.MuiSelect-select)': {
                                borderRadius: '32px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderRadius: '32px',
                                },
                            },
                        },
                    },
                },
                MuiFormControl: {
                    styleOverrides: {
                        root: {
                            marginTop: '8px',
                            gap: '8px 8px',
                            '&:first-child': {
                                marginTop: '0px',
                            },
                        },
                    },
                },
                MuiTypography: {
                    styleOverrides: {
                        h1: {
                            fontFamily: 'Bricolage Grotesque, Inter, Arial, sans-serif',
                        },
                        caption: {
                            color: isDarkMode ? '#b0b0b0' : '#666666',
                        },
                        subtitle1: {
                            color: isDarkMode ? '#b0b0b0' : '#666666',
                        },
                        subtitle2: {
                            color: isDarkMode ? '#b0b0b0' : '#666666',
                        },
                        h2: {
                            fontFamily: 'Bricolage Grotesque, Inter, Arial, sans-serif',
                        },
                        h3: {
                            fontFamily: 'Bricolage Grotesque, Inter, Arial, sans-serif',
                        },
                        h4: {
                            fontFamily: 'Bricolage Grotesque, Inter, Arial, sans-serif',
                        },
                        h5: {
                            fontFamily: 'Bricolage Grotesque, Inter, Arial, sans-serif',
                        },
                        h6: {
                            fontFamily: 'Bricolage Grotesque, Inter, Arial, sans-serif',
                        },
                    },
                },
                MuiToolbar: {
                    styleOverrides: {
                        root: {
                            '@media (min-width: 600px)': {
                                marginLeft: '8px',
                                marginRight: '24px',
                            },
                        },
                    },
                },
                MuiSelect: {
                    defaultProps: {
                        displayEmpty: true,
                        variant: 'outlined',
                        size: 'medium',
                    },
                    styleOverrides: {
                        root: {
                            '&.MuiOutlinedInput-root': {
                                borderRadius: '32px',
                                backgroundColor: 'transparent',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderRadius: '32px',
                                },
                            },
                            '& .MuiSelect-select': {
                                padding: '8px 56px 8px 17px !important',
                            },
                            '@media (max-width: 600px)': {
                                '& .MuiSelect-select': {
                                    padding: '8px 48px 8px 12px !important',
                                    fontSize: '0.875rem',
                                },
                            },
                        },
                    },
                },
                MuiChip: {
                    styleOverrides: {
                        root: {
                            // Theme-aware icon color inheritance
                            '& .MuiChip-icon': {
                                color: 'inherit',
                            },
                            // Legacy support
                            '&.status-chip-accepted': {
                                backgroundColor: '#0FC89D',
                                color: 'white',
                                height: '32px',
                                borderRadius: '16px',
                                fontWeight: 500,
                                '& .MuiChip-icon': {
                                    color: 'white',
                                },
                                '&:hover': {
                                    backgroundColor: '#0FC89D',
                                },
                            },
                            '&.status-chip-rejected': {
                                backgroundColor: isDarkMode ? lighten(primaryColor, 0.2) : primaryColor,
                                color: 'white',
                                height: '32px',
                                borderRadius: '16px',
                                fontWeight: 500,
                                '& .MuiChip-icon': {
                                    color: 'white',
                                },
                                '&:hover': {
                                    backgroundColor: isDarkMode ? lighten(primaryColor, 0.2) : primaryColor,
                                },
                            },
                        },
                    },
                    variants: [
                        {
                            props: { variant: 'gradientChip' },
                            style: {
                                background: isDarkMode
                                    ? `linear-gradient(135deg, ${alpha(primaryColor, 0.3)} 0%, ${alpha(secondaryColor, 0.2)} 100%)`
                                    : `linear-gradient(135deg, ${alpha(primaryColor, 0.1)} 0%, ${alpha(secondaryColor, 0.05)} 100%)`,
                                border: `1px solid ${alpha(primaryColor, 0.3)}`,
                                color: isDarkMode ? '#ffffff' : primaryColor,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '& .MuiChip-icon': {
                                    color: isDarkMode ? '#ffffff' : primaryColor,
                                },
                                '&:hover': {
                                    background: isDarkMode
                                        ? `linear-gradient(135deg, ${alpha(primaryColor, 0.5)} 0%, ${alpha(secondaryColor, 0.4)} 100%)`
                                        : `linear-gradient(135deg, ${alpha(primaryColor, 0.2)} 0%, ${alpha(secondaryColor, 0.1)} 100%)`,
                                    transform: 'scale(1.05)',
                                    boxShadow: `0 2px 8px ${alpha(primaryColor, 0.3)}`,
                                },
                            },
                        },
                    ],
                },
                MuiInputLabel: {
                    styleOverrides: {
                        root: {},
                    },
                },
                MuiListItem: {
                    styleOverrides: {
                        root: {
                            padding: '4px',
                        },
                    },
                },

                MuiTableCell: {
                    styleOverrides: {
                        root: {
                            height: '75px',
                            '@media (min-width: 1200px)': {
                                padding: '24px',
                            },
                        },
                        head: {
                            ...(!isDarkMode && {
                                backgroundColor: '#ffffff',
                            }),
                        },
                    },
                },
                MuiTableRow: {
                    styleOverrides: {
                        root: {
                            height: '75px',
                            '&:hover': {
                                backgroundColor: `${alpha(isDarkMode ? lighten(primaryColor, 0.2) : primaryColor, 0.2)} !important`,
                            },
                            '&.Mui-selected': {
                                backgroundColor: `${alpha(isDarkMode ? lighten(primaryColor, 0.2) : primaryColor, 0.2)} !important`,
                            },
                        },
                    },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            borderRadius: '12px',
                            border: `1px solid ${isDarkMode ? alpha('#ffffff', 0.1) : alpha('#000000', 0.08)}`,
                        },
                    },
                },
                MuiTooltip: {
                    styleOverrides: {
                        tooltip: {
                            backgroundColor: isDarkMode ? alpha(grey[900], 0.95) : alpha(grey[700], 0.95),
                            color: '#ffffff',
                            fontSize: '0.75rem',
                            fontWeight: 400,
                            padding: '4px 8px',
                            borderRadius: '6px',
                            boxShadow: `0 2px 8px ${alpha(common.black, 0.15)}`,
                            backdropFilter: 'blur(8px)',
                            border: `1px solid ${alpha(isDarkMode ? '#ffffff' : '#000000', 0.1)}`,
                            maxWidth: 200,
                            lineHeight: 1.4,
                        },
                        arrow: {
                            color: isDarkMode ? alpha(grey[900], 0.95) : alpha(grey[700], 0.95),
                        },
                    },
                    defaultProps: {
                        placement: 'top',
                        arrow: false,
                        enterDelay: 300,
                        leaveDelay: 0,
                        enterTouchDelay: 700,
                    },
                },
                MuiCssBaseline: {
                    styleOverrides: {
                        '@supports (font-variation-settings: normal)': {
                            html: { fontFamily: 'InterVariable, sans-serif' },
                        },
                        html: {
                            fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"',
                            WebkitFontSmoothing: 'antialiased',
                            MozOsxFontSmoothing: 'grayscale',
                        },
                        div: {
                            color: isDarkMode ? '#dfdfd6' : '#333333',
                        },
                        // Global mobile spacing for content containers
                        '@media (max-width: 600px)': {
                            '.mobile-content, .MuiPaper-root, .MuiCard-root, .content-container': {
                                marginLeft: '16px !important',
                                marginRight: '16px !important',
                            },
                        },
                        // Header icon button styling
                        '.header-icon-button': {
                            color: `${isDarkMode ? lighten(primaryColor, 0.2) : primaryColor} !important`,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                backgroundColor: `${alpha(isDarkMode ? lighten(primaryColor, 0.2) : primaryColor, 0.1)} !important`,
                                transform: 'scale(1.05)',
                            },
                        },
                        // SubtitleHeader styling
                        '.SubtitleHeader': {
                            fontFamily: 'Bricolage Grotesque, Inter, Arial, sans-serif',
                            fontWeight: 400,
                            fontSize: '1rem',
                            lineHeight: 1.4,
                            color: isDarkMode ? '#a0a0a0' : '#999999',
                            '@media (max-width: 600px)': {
                                fontSize: `${1.5 / 1.618}rem`,
                            },
                        },
                        // MuiSelectorOutlined styling
                        '.MuiSelectorOutlined': {
                            padding: '8px 14px',
                            height: '40px',
                            minHeight: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            boxSizing: 'border-box',
                        },
                        // Unified Select styling
                        '.unified-select': {
                            '& .MuiSelect-select': {
                                padding: '8px 14px !important',
                                fontSize: '0.875rem',
                                fontWeight: 400,
                                display: 'flex',
                                alignItems: 'center',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderRadius: '12px',
                            },
                        },
                    },
                },
            },
        });

        theme = responsiveFontSizes(theme);
        return theme;
    }, [isDarkMode, primaryColor, secondaryColor, defaultColors]);
}
